import React, {useState} from 'react';

import {assert} from 'Asserts';
import {ProcedureRunDataEntryMode, ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ExperimentalInputs,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {
  Condition,
  useConditionsAndExperimentalInputs,
  useExperimentalInputsContextProperties,
} from 'studies/typed/process-development/useProcessDevelopmentStudyState';
import useStateIfMounted from 'ui-library/hooks/useStateIfMounted';

import {EditNumConditionsButtons} from './EditNumConditionsButtons';
import {ExperimentFlowchart, LargestTableTypeSizes} from './ExperimentalInputs';
import {StudyDesignTabEditState} from './StudyDesignTabSavingButtons';
import {MethodExecutionLimitState, useMethodExecutionLimitState} from './useMethodExecutionLimitState';

/**
 * Represents the flowchart version with required properties for study design.
 */
export interface StudyDesignFlowchartVersion {
  id: string;
  largestTableTypeSizes: ReadonlyArray<{tableKind: string; size: number}>;
}

/**
 * Parameters for the useStudyDesignState hook.
 */
export interface UseStudyDesignStateParams {
  /** Initial experimental inputs extracted from procedure run or plan */
  initialExperimentalInputs: ExperimentalInputs;
  /** Template flowchart with method nodes */
  templateFlowchart: ExperimentFlowchart;
  /** Initial conditions (from procedure run conditions or plan conditions) */
  initialConditions: Array<Condition>;
  /** Procedure type (RECIPE or ASSAY) */
  procedureType: ProcedureType;
  /** Flowchart version with table size info */
  flowchartVersion: StudyDesignFlowchartVersion;
  /** Data entry mode for the run/plan */
  dataEntryMode: ProcedureRunDataEntryMode;
  /** Procedure ID */
  procedureId: string;
}

/**
 * Result of the useStudyDesignState hook.
 */
export interface UseStudyDesignStateResult {
  // UI state
  selectedMethodId: string | null;
  setSelectedMethodId: (id: string) => void;
  editState: StudyDesignTabEditState;
  setEditState: (state: StudyDesignTabEditState) => void;
  selectedDesignTabIndex: number | null;
  setSelectedDesignTabIndex: (index: number | null) => void;
  sidepanelName: string;
  setSidepanelName: (name: string) => void;

  // Conditions and inputs
  conditions: Array<Condition>;
  setConditions: (conditions: Array<Condition>, prevIndexes: Array<number | null>) => void;
  experimentalInputs: ExperimentalInputs;
  setExperimentalInputs: (inputs: ExperimentalInputs) => void;
  initialConditions: Array<Condition>;

  // Derived state
  context: ExperimentalInputsContextProperties;
  methodExecutionLimitState: MethodExecutionLimitState;
  templateFlowchart: ExperimentFlowchart;

  // UI elements
  editNumColsButtons: React.ReactNode;
}

const PARAMETER_VALUES_TAB_NAME = 'Conditions';

/**
 * Hook that encapsulates shared state management for study design tabs.
 * Used by both MultiProcedureRunStudyDesignTabContents and MultiProcedureRunPlanStudyDesignTabContents.
 */
export function useStudyDesignState(params: UseStudyDesignStateParams): UseStudyDesignStateResult {
  const {
    initialExperimentalInputs,
    templateFlowchart,
    initialConditions,
    procedureType,
    flowchartVersion,
    dataEntryMode,
    procedureId,
  } = params;

  // UI state
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(
    templateFlowchart.nodes[0]?.id ?? null
  );
  // Use useStateIfMounted because setEditState may be called in async callbacks
  const [editState, setEditState] = useStateIfMounted(StudyDesignTabEditState.SAVED);
  const [selectedDesignTabIndex, setSelectedDesignTabIndex] = useState<number | null>(0);
  const [sidepanelName, setSidepanelName] = useState<string>(PARAMETER_VALUES_TAB_NAME);

  // Conditions and experimental inputs state
  const {conditions, setConditions, experimentalInputs, setExperimentalInputs} =
    useConditionsAndExperimentalInputs(
      {
        id: procedureId,
        procedureType,
        methods: templateFlowchart.nodes,
      },
      procedureId,
      initialConditions,
      initialExperimentalInputs
    );

  // useConditionsAndExperimentalInputs returns null when procedure ID is null,
  // which should never be the case when using this hook
  assert(
    conditions != null && experimentalInputs != null,
    'Expected conditions and experimentalInputs to be non-null'
  );

  // Context for ExperimentalInputs components
  const context = useExperimentalInputsContextProperties(
    conditions,
    experimentalInputs,
    setExperimentalInputs,
    templateFlowchart,
    procedureType,
    // Should be locked when not in editing state
    editState !== StudyDesignTabEditState.EDITING
  );
  assert(context != null, 'Expected context to be non-null');

  // Method execution limit state
  const methodExecutionLimitState = useMethodExecutionLimitState(
    experimentalInputs,
    conditions,
    flowchartVersion.largestTableTypeSizes as LargestTableTypeSizes,
    dataEntryMode
  );

  // Edit buttons for conditions
  const editNumColsButtons = (
    <EditNumConditionsButtons
      conditions={conditions}
      setConditions={setConditions}
      methodExecutionLimitState={methodExecutionLimitState}
      procedureType={procedureType}
    />
  );

  return {
    // UI state
    selectedMethodId,
    setSelectedMethodId,
    editState,
    setEditState,
    selectedDesignTabIndex,
    setSelectedDesignTabIndex,
    sidepanelName,
    setSidepanelName,

    // Conditions and inputs
    conditions,
    setConditions,
    experimentalInputs,
    setExperimentalInputs,
    initialConditions,

    // Derived state
    context,
    methodExecutionLimitState,
    templateFlowchart,

    // UI elements
    editNumColsButtons,
  };
}

/**
 * Returns an array of indices indicating the positions of each initial condition
 * within the full list of conditions, based on matching condition IDs.
 * This is necessary to handle the case where the user reorders the persisted conditions.
 */
export function getInitialConditionIndices(
  initialConditions: Array<Condition>,
  conditions: Array<Condition>
): Array<number> {
  return initialConditions.map((initialCondition) =>
    conditions.findIndex((c) => c.id === initialCondition.id)
  );
}
